import { faUserCheck, faUserPlus } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Banner, Modal, ModalBody, ModalFooter, ModalHeader } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ConfirmButton } from '@/components/Buttons';
import { ConfirmDialog } from '@/components/dialogs';
import { UserIcon } from '@/components/Icons';
import Indicator from '@/components/Indicator';
import { DateDistanceDisplay } from '@/components/localization';
import React, { Fragment, PropsWithChildren, ReactNode, useState } from 'react';
import { cancelResidentialUnitBuyerInvitation, removeResidentialUnitBuyer } from '@/services/residential-units';
import { BuyerInfo, BuyerInfoUser, InvitationStatus } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { classnames } from '@/utils/html';
import BuyerInviteResendDialog from './BuyerInviteResendDialog';
import theme from './BuyerProfileInfoDialog.module.scss';
import ResidentialUnitInviteBuyerDialog from './ResidentialUnitInviteBuyerDialog';

interface BuyerUserRowProps {
    user: BuyerInfoUser;
    residentialUnitId: number;
    onBuyerRemoved?(user: BuyerInfoUser): void;
    onInviteCanceled?(user: BuyerInfoUser): void;
    onBuyerUpdated?(user: BuyerInfoUser): void;
}

const BuyerUserRow: React.FC<BuyerUserRowProps> = ({
    user,
    residentialUnitId,
    onBuyerRemoved,
    onInviteCanceled,
    onBuyerUpdated,
}) => {
    const [inviteResent, setInviteResent] = useState(false);
    const [showResendDialog, setShowResendDialog] = useState(false);
    const [removingBuyer, setRemovingBuyer] = useState(false);
    const [rowState, setRowState] = useState<'normal' | 'canceled' | 'removed'>('normal');
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [currentInviteStatus, setCurrentInviteStatus] = useState(user.inviteStatus);
    const fullName = `${user.salutation ?? ''} ${user.title ?? ''} ${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

    const handleRemoveBuyerClick = async () => {
        setRemovingBuyer(true);

        try {
            await removeResidentialUnitBuyer(residentialUnitId, user.userId!);
            setRowState('removed');
            triggerHandler(onBuyerRemoved, user);
        } finally {
            setRemovingBuyer(false);
        }
    };

    const handleCancelInviteClick = async () => {
        setRemovingBuyer(true);

        try {
            await cancelResidentialUnitBuyerInvitation(user.inviteId);
            triggerHandler(onInviteCanceled, user);
            setRowState('canceled');
        } finally {
            setRemovingBuyer(false);
        }
    };

    const renderStatus = () => {
        if (rowState === 'canceled') {
            return (
                <Indicator color="danger">
                    <Trans>Canceled</Trans>
                </Indicator>
            );
        }

        if (rowState === 'removed') {
            return (
                <Indicator color="danger">
                    <Trans>Removed</Trans>
                </Indicator>
            );
        }

        if (user.acceptedDate) {
            return (
                <Indicator color="success">
                    <Trans>Accepted</Trans>
                </Indicator>
            );
        }

        if (currentInviteStatus === InvitationStatus.Pending) {
            return (
                <Indicator color="info">
                    <Trans>Not invited</Trans>
                </Indicator>
            );
        }

        return (
            <Indicator color="warning">
                <Trans>Invitation sent</Trans>
            </Indicator>
        );
    };

    const renderResendInvitation = () => {
        if (inviteResent) {
            return (
                <small>
                    <Trans>Invitation sent</Trans>
                </small>
            );
        }

        if (user.invitedDate && !user.acceptedDate && rowState === 'normal') {
            return (
                <div className={theme.invitationLinkContainer}>
                    <Button
                        color="link"
                        size="xsmall"
                        onClick={(e) => setShowResendDialog(true)}
                        className={theme.invitationLink}>
                        {currentInviteStatus === InvitationStatus.Pending ? (
                            <Trans>Send invitation</Trans>
                        ) : (
                            <Trans>Resend invitation</Trans>
                        )}
                    </Button>
                    {currentInviteStatus === InvitationStatus.Pending && (
                        <Button
                            color="link"
                            size="xsmall"
                            onClick={(e) => setShowEditDialog(true)}
                            className={theme.editLink}>
                            <Trans>Edit</Trans>
                        </Button>
                    )}
                </div>
            );
        }
    };

    const renderRemoveButton = () => {
        if (user.acceptedDate) {
            return (
                <ConfirmButton
                    dialogTitle={<Trans>Remove buyer</Trans>}
                    dialogBody={
                        <>
                            <Banner type="warning">
                                <Trans>
                                    Do not remove buyers if they have trouble logging in or accepting invitations, only
                                    remove them if they have nothing to do with this unit anymore.
                                </Trans>
                                <br />
                                <br />
                                <u>
                                    <Trans>If the buyer has login problems, contact customer support</Trans>
                                </u>
                                <br />
                                <a href="mailto:Support@rubustech.io">Support@rubustech.io</a>
                            </Banner>
                            <br />
                            <Trans>Are you sure you want to remove {fullName} as a buyer of this unit?</Trans>
                        </>
                    }
                    color="danger"
                    loading={removingBuyer}
                    disabled={rowState !== 'normal'}
                    onConfirm={handleRemoveBuyerClick}
                    dialogConfirmButtonColor="danger">
                    <Trans>Remove buyer</Trans>
                </ConfirmButton>
            );
        }

        return (
            <ConfirmButton
                dialogTitle={<Trans>Cancel invitation</Trans>}
                dialogBody={<Trans>Are you sure you want to cancel this invitation?</Trans>}
                color="danger"
                loading={removingBuyer}
                disabled={rowState !== 'normal'}
                onConfirm={handleCancelInviteClick}
                dialogConfirmButtonColor="danger">
                {currentInviteStatus === InvitationStatus.Pending ? (
                    <Trans>Remove buyer</Trans>
                ) : (
                    <Trans>Cancel invite</Trans>
                )}
            </ConfirmButton>
        );
    };
    return (
        <Fragment key={user.inviteId}>
            <BuyerInviteResendDialog
                buyers={[user]}
                show={showResendDialog}
                onClose={() => setShowResendDialog(false)}
                onAccept={(sentInvites) => {
                    setInviteResent(true);
                    // Update status from Pending to Sent if invitation was sent
                    if (sentInvites.length > 0 && currentInviteStatus === InvitationStatus.Pending) {
                        setCurrentInviteStatus(InvitationStatus.Sent);
                    }
                }}
            />

            <ResidentialUnitInviteBuyerDialog
                residentialUnitId={residentialUnitId}
                show={showEditDialog}
                onClose={() => setShowEditDialog(false)}
                onAccept={(options) => {
                    setShowEditDialog(false);
                    if (options?.invitationSent && currentInviteStatus === InvitationStatus.Pending) {
                        setCurrentInviteStatus(InvitationStatus.Sent);
                    }
                    triggerHandler(onBuyerUpdated, user);
                }}
                editMode={true}
                existingInvitation={{
                    inviteId: user.inviteId,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    phoneNumber: user.phoneNumber,
                    title: user.title,
                    salutation: user.salutation,
                }}
            />

            <Section>
                <UserIcon
                    emptyIcon={user.invitedDate && !user.acceptedDate && rowState === 'normal' ? faUserCheck : undefined}
                    className={theme.buyerImage}
                    sizeInPx={36}
                    userId={user.userId || 0}
                />
            </Section>
            <Section caption={<Trans>Buyer</Trans>}>
                <div className={theme.buyerName}>
                    <span>{fullName}</span>

                    {renderResendInvitation()}

                    {user.lastVisitDate && (
                        <small>
                            <DateDistanceDisplay
                                prefix={
                                    <>
                                        <Trans>Active:</Trans>&nbsp;
                                    </>
                                }
                                value={user.lastVisitDate}
                            />
                        </small>
                    )}
                </div>
            </Section>
            <Section caption={<Trans>Email</Trans>}>
                <a href={`mailto:${user.email}`}>{user.email}</a>
            </Section>
            <Section caption={<Trans>Phone</Trans>}>
                <a href={`tel:${user.phoneNumber}`}>{user.phoneNumber}</a>
            </Section>
            <Section caption={<Trans>Status</Trans>}>{renderStatus()}</Section>
            <Section className={theme.removeSection}>{renderRemoveButton()}</Section>

            <hr className={theme.separator} />
        </Fragment>
    );
};

interface SectionProps extends PropsWithChildren {
    caption?: ReactNode;
    className?: string;
}

const Section: React.FC<SectionProps> = ({ children, caption, className }) => {
    return (
        <div className={classnames(theme.section, className)}>
            {caption && <div className={theme.caption}>{caption}</div>}
            <div className={theme.content}>{children}</div>
        </div>
    );
};

interface BuyerProfileInfoDialogProps {
    show: boolean;
    buyerInfo: BuyerInfo;
    residentialUnitId: number;
    onClose(shouldReload: boolean): void;
    onBuyerAdded(): void;
}

const BuyerProfileInfoDialog: React.FC<BuyerProfileInfoDialogProps> = ({
    show,
    buyerInfo,
    residentialUnitId,
    onClose,
    onBuyerAdded,
}) => {
    const [needsReload, setNeedsReload] = useState(false);
    const [showInviteBuyerDialog, setShowInviteBuyerDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const onConfirm = async () => {
        setShowConfirmDialog(false);
        setShowInviteBuyerDialog(true);
    };

    const onOpen = () => setNeedsReload(false);
    const handleCloseClick = () => triggerHandler(onClose, needsReload);
    const handleChange = () => setNeedsReload(true);
    const handleAddBuyerClick = () => setShowConfirmDialog(true);
    const handleBuyerAdded = () => onBuyerAdded();
    const handleBuyerUpdated = () => onBuyerAdded();

    return (
        <Modal isOpen={show} toggle={handleCloseClick} onOpen={onOpen} size="lg">
            <ModalHeader>
                <Trans>Buyer info</Trans>
            </ModalHeader>
            <ModalBody className={theme.body}>
                <div className={theme.grid}>
                    {buyerInfo.users.map((user) => (
                        <BuyerUserRow
                            key={user.inviteId}
                            user={user}
                            residentialUnitId={residentialUnitId}
                            onBuyerRemoved={handleChange}
                            onInviteCanceled={handleChange}
                            onBuyerUpdated={handleBuyerUpdated}
                        />
                    ))}
                </div>

                <ResidentialUnitInviteBuyerDialog
                    onClose={() => setShowInviteBuyerDialog(false)}
                    onAccept={handleBuyerAdded}
                    show={showInviteBuyerDialog}
                    residentialUnitId={residentialUnitId}
                />

                <ConfirmDialog
                    title={t`Add another buyer`}
                    body={
                        <Banner type="warning" className="pre-wrap">
                            <Trans>
                                Please note! When adding an extra buyer to a unit, they will see the same chat history as the
                                existing buyer. <br />
                                If the unit has been sold you should first remove all existing buyers on this unit.
                                <br />
                                <br />
                                Are you sure you want to add an extra buyer to this unit?
                            </Trans>
                        </Banner>
                    }
                    show={showConfirmDialog}
                    onClose={() => setShowConfirmDialog(false)}
                    onConfirm={onConfirm}
                />
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCloseClick} className="left">
                    <Trans>Close</Trans>
                </Button>
                <Button color="tertiary" onClick={handleAddBuyerClick} icon={faUserPlus}>
                    <Trans>Add another buyer</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BuyerProfileInfoDialog;
